/**
 * Nanome look constants that are not tables. Each block names its source under vendor-ref/nanome.
 * Bump LOOK_SPEC_VERSION whenever a value here or in generated/ is re-transcribed.
 */
export const LOOK_SPEC_VERSION = '2026-10-03/nanome@868d90f';

/** Units: 1 Å = 0.02 Unity metres (RenderingViewModel.cs:44). Domain→Unity swaps y and z (Adapter/Math/Conversion.cs:8-11); in a right-handed GL frame that is (x, z, -y). */
export const ANGSTROM_TO_METRE = 0.02;

/** Atomistic (Nanome.Domain/Scenes/Components/Visualizations/Atomistic.cs:37-43). Sphere radius rule (RenderingViewModel.Atomistic.cs:1381-1390): Uniform → scale; Physical → vdW[element] * scale; Disorder → normalised B. */
export const ATOMISTIC = {
  spacefill: { sizeOption: 'Physical', scale: 1.0, renderAtom: 'All', renderBond: false, bondScale: 0 },
  ballAndStick: { sizeOption: 'Physical', scale: 0.25, renderAtom: 'All', renderBond: true, bondScale: 0.2 },
  stick: { sizeOption: 'Uniform', scale: 0.2, renderAtom: 'All', renderBond: true, bondScale: 0.2 },
  wire: { sizeOption: 'Uniform', scale: 0.1, renderAtom: 'NonBondedOnly', renderBond: true, bondScale: 0.05 },
} as const;

/** Multiple-bond layout (RenderingViewModel.Atomistic.cs:28-34, 1362-1378). minR = smaller sphere radius of the two atoms. */
export const BOND_LAYOUT = {
  doubleSpacing: 0.1, doubleMinSizing: 0.025,
  tripleSpacing: 0.2, tripleMinSizing: 0.015,
  aromaticSpacing: 0.35, aromaticSizing: 0.7, aromaticOffset: 0.4,
  /** double: scale = max(doubleMinSizing, bondScale*minR*1.25); spacing = min(doubleSpacing, minR - scale) */
  /** triple: scale = max(tripleMinSizing, bondScale*minR); spacing = min(tripleSpacing, minR - scale) */
  /** aromatic: full cylinder + inner cylinder at aromaticSizing×radius, inset aromaticOffset Å, shifted aromaticSpacing Å toward the ring centroid */
  /** bond colour splits at the cylinder midpoint (ImpostorCylinder.hlsl:262-266) */
} as const;

/** Ribbon / cartoon (RibbonBuilder.cs:124-135, 320-347, 350-422, 532-588; RenderingViewModel.Ribbon.cs:24, 931-943; RenderingQualityConfig.cs:8-11). */
export const RIBBON = {
  /** uniform cubic B-spline basis rows, each /6 */
  bsplineBasis: [[-1, 3, -3, 1], [3, -6, 3, 0], [-3, 0, 3, 0], [1, 4, 1, 0]] as const,
  stepsPerResidue: { high: 8, medium: 4, low: 2, minimum: 1 } as const,
  width: 6.0, tubeSize: 0.85, radius: 0.15,
  /** helix/sheet: half-width 0.9 Å (= width*radius), half-height 0.075 Å */
  helixHalfWidth: 0.9, helixHalfHeight: 0.075,
  /** last residue of a sheet: arrow head half-width 0.9*1.75 */
  arrowFactor: 1.75, arrowHalfWidth: 1.575,
  /** coil: square tube of half size radius*tubeSize, rounded normals (CoilPerPoint) */
  coilHalfSize: 0.1275,
  /** gap when residue ids are not consecutive or Cα–Cα >= 5 Å */
  gapDistance: 5.0,
  /** nucleic: C5' trace, C3' second position, N1 (G) or N3 (others) base direction, base slabs half-size 0.2 */
  nucleicBaseHalfSize: 0.2,
  /** SS → style: Helix3→Helix3, Helix5→Helix5, AlphaHelix→Helix, Nucleic_Helix→NucleicHelix, Strand|BetaBridge→Sheet, else Coil */
  /** reproduce the sheet-smoothing quirk: control point is effectively (3p + last)/4 because nextPosition1 never advances (RibbonBuilder.cs:396,413) */
  reproduceSheetSmoothingQuirk: true,
  material: { smoothness: 0.15, metallic: 0 },
} as const;

/**
 * Lighting (Environment.prefab:252-258 and 260-320, Main.unity:17-41, Universal Render Pipeline Asset.asset:26-69,
 * SphereImpostorWebGL.mat:28-32, RibbonMaterial.mat:118, SurfaceMaterialOpaque.mat:108). Checked against pixels read
 * from app.nanome.ai on 2026-10-03: docs/research/lighting-parity.md.
 */
export const LIGHTING = {
  /** one directional light, white, intensity 1, Unity Euler (50, -30, 0), fixed relative to the CAMERA (the trackball moves the scene) */
  directionalEulerDeg: [50, -30, 0] as const,
  directionalIntensity: 1,
  /** Main.unity:23,26-27: m_AmbientMode 3 (Flat), m_AmbientSkyColor 0.666 (sRGB), m_AmbientIntensity 1. Nothing sets RenderSettings.ambientMode at runtime, so SampleSH is this constant for every normal. */
  ambientFlat: 0.666,
  /** impostor Blinn-Phong (ImpostorCommon.hlsl:138-151): exponent 64 (= exp2(10*0.5+1)), specular colour 0.2 grey */
  impostorSpecularExponent: 64, impostorSpecularColor: 0.2,
  impostorMinPixelRadius: 1.25,
  /**
   * Ribbons and surfaces are URP Lit shader graphs, metallic workflow, metallic 0 (Lit_VertexColor.shadergraph:
   * UniversalLitSubTarget m_WorkflowMode 1). URP 17.3.0 (Packages/manifest.json:28) BRDF.hlsl: kDielectricSpec is 0.04,
   * so the diffuse colour is albedo * (1 - 0.04) and the specular colour is 0.04.
   */
  urpDielectricSpec: 0.04,
  /** RibbonMaterial.mat:118 and :109 */
  ribbonMaterial: { smoothness: 0.15, metallic: 0 },
  /** SurfaceMaterialOpaque.mat:108 and :102 (the transparent and back-face variants carry the same values) */
  surfaceMaterial: { smoothness: 0.3, metallic: 0 },
  /** Main.unity:35,38: reflections come from the baked skybox at intensity 0.8 (EnvironmentManager.cs:72-75). Lite leaves this term out; see lighting-parity.md. */
  reflectionIntensity: 0.8,
  msaa: 4, hdr: false, fog: false,
  shadows: { enabled: true, mapSize: 2048, cascades: 1, soft: true, distanceMetres: 1 /* = 50 Å */ },
  /** shipped WebGL takes impostor shadow depth from the rasterised proxy quads, not analytic depth (ImpostorCommon.hlsl:19-25) */
  impostorShadowRasterDepth: true,
  linearColorSpace: true,
} as const;

/** Camera and framing (ScreenRig.prefab, SceneAutoZoom.cs:23-29, 83-131). */
export const CAMERA = {
  fovDeg: 60, near: 0.1, far: 100,
  autoZoom: { focalMetres: 0.4, fill: 0.8, scaleMin: 0.002, scaleMax: 0.2 },
} as const;

/** Trackball (TrackballControl.cs:28-32, 390-465, 470-560). Moves the scene transform, not the camera. */
export const TRACKBALL = {
  rotateDegPerPx: 1, panMetresPerPx: 0.0005, zoomBase: 1.01, zoomRange: [0.1, 10] as const,
  zoomPivotHoldSeconds: 0.5, touchPinchFactor: 0.1, touchLockPx: 10, touchDecay: 0.85,
  /** pivot = selection centre ?? hovered atom ?? centre of the entry under the cursor ?? origin */
} as const;

/** Hover, selection and labels (WorkspaceSceneDiffViewModel.cs:42-49, RenderingViewModel.Atomistic.cs:36-39, 631-646, RibbonRenderer.cs:37-64, HighlightLabel.prefab, ResidueLabel.prefab). */
export const INTERACTION = {
  hoverColor: 0x463dff, removeWarningColor: 0xf9ff4d, selectionColor: 0xffffff,
  atomOutlineShellAngstrom: 0.1, ribbonInverseHullSize: 0.1,
  /** entry hover wireframe box shown when the entry covers less than this fraction of the screen */
  entryHoverBoxCoverage: 0.75,
  hoverLabel: { fontSize: 10, scale: 0.3 }, residueLabel: { fontSize: 12, scale: 0.6 },
  /** "<b>{entry≤7}</b>\n<b>{chain}</b>|{resn}'{resi}\n{atomName}"; the ribbon variant drops the atom line; residue labels are "{ResName} {ResSerial}" at the residue centroid */
  entryNameEllipsis: 7,
} as const;

/** Image export (ImageCaptureRenderer.cs:12-20, 199-229; WorkspaceSnapshotPopup.vue:5-19). */
export const SNAPSHOT = {
  presets: [
    { label: 'Single column', width: 1050, height: 788 },
    { label: '1.5 column', width: 1575, height: 1181 },
    { label: 'Double column', width: 2100, height: 1575 },
    { label: 'Presentation', width: 1920, height: 1080 },
    { label: '4K', width: 3840, height: 2160 },
    { label: '8K', width: 7680, height: 4320 },
  ] as const,
  defaultIndex: 4,
  maxEdge: 8192, maxEdgeMobile: 2048, supersampleBelowEdge: 4096, supersample: 2,
  includeBackgroundDefault: false,
} as const;

/** Molecular surface reference (ComputeMolecularSurface.cs:81-200, SurfaceComputation.cs:252-255): MSMS probe 1.4, density 6.0, vdW radii, per chain and per connected component. */
export const SURFACE = {
  probeRadius: 1.4, msmsDensity: 6.0,
  /** SESAM edge length equivalent to MSMS density d: sqrt(2 / (sqrt(3) * d)) */
  edgeForDensity: (d: number) => Math.sqrt(2 / (Math.sqrt(3) * d)),
  defaultOpacity: 0.5,
} as const;

/** Secondary structure enum (Nanome.Domain/Model/Properties/SecondaryStructure.cs:7-29). */
export const SecondaryStructure = {
  NotAssigned: -1, Loop: 0, Turn: 1, AlphaHelix: 2, Strand: 3, BetaBridge: 4, Helix3: 5, Helix5: 6, Bend: 7,
  Nucleic_Single: 8, Nucleic_Double: 9, Nucleic_Helix: 10, Nucleic_StemLoop: 11, Nucleic_PseudoKnot: 12, Nucleic_Bulge: 13, Nucleic_HairpinLoop: 14, Nucleic_InternalLoop: 15, Nucleic_MultiLoop: 16,
} as const;
export type SecondaryStructureType = (typeof SecondaryStructure)[keyof typeof SecondaryStructure];

/** Substructure filter flags (WorkspaceAPI FilterModel ESubstructureFilterType). */
export const SubstructureFlag = { Protein: 1, DNA: 2, RNA: 4, PNA: 8, Ligand: 16, Saccharide: 32, Ion: 64, Solvent: 128 } as const;
export const SUBSTRUCTURE_NUCLEIC = SubstructureFlag.DNA | SubstructureFlag.RNA | SubstructureFlag.PNA;
export const SUBSTRUCTURE_POLYMER = SubstructureFlag.Protein | SUBSTRUCTURE_NUCLEIC;

/** Default pocket distance around a ligand (AddDefaultComponents.cs). */
export const DEFAULT_POCKET_DISTANCE = 4.0;
